<script lang="ts">
  import { getVideoStyle, styleToString } from "@shared/common/node/style";
  import type {
    IImageNode,
    IVideoNode,
    PartialNode,
  } from "@shared/common/node/type";
  import type { VideoInfo } from "@shared/common/types/data-source";
  import { getDataSourceValue } from "@shared/common/utils/data-source";
  import { assertCondition, isObject } from "@shared/common/utils/value";
  import type HlsType from "hls.js";
  import { onDestroy, onMount } from "svelte";
  import detectReaction from "../../actions/detect-reaction";
  import {
    getCurrentDocumentNode,
    getSlideNodeIndexContext,
    getStore,
  } from "../../context";

  const {
    dataMap,
    captureMode,
    reactionEventListener,
    swiperInstance,
    swiperRealIndex,
    swiperOptions,
    swiperLength,
    rootShown,
  } = getStore();
  const documentNode = getCurrentDocumentNode();
  const slideNodeIndex = getSlideNodeIndexContext();

  export let node: PartialNode<IVideoNode | IImageNode>;
  let video: HTMLVideoElement | null = null;
  let HlsInstance: typeof HlsType | null = null;
  let hls: HlsType | null = null;
  let load = false;
  let isPlayable = false;
  let isPlaying = false;
  let isShown = false;

  const videoInfoAssertion = assertCondition<VideoInfo[]>(
    (val): val is VideoInfo[] =>
      Array.isArray(val) &&
      val.every(
        (item) => isObject(item) && "status" in item && "videoUrl" in item
      )
  );

  $: videoSrc = videoInfoAssertion
    .typeSafeValue(
      getDataSourceValue({
        dataMap: $dataMap ?? {},
        keyString: "video",
      }),
      []
    )
    .find((video) => video.filename === node.videoInfo?.fileName);

  $: autoplay = node?.autoplay;
  $: loop = node?.loop;
  $: controls = node?.controls;
  $: muted = node?.muted;

  onMount(async () => {
    const { default: HlsModule } = await import("hls.js");
    HlsInstance = HlsModule;
    load = true;
  });

  $: {
    if (videoSrc?.status === "COMPLETE" && load === true) {
      if (HlsInstance && HlsInstance.isSupported()) {
        initializeHls(HlsInstance);
      } else if (video?.canPlayType("application/vnd.apple.mpegurl")) {
        setupNativeHls();
      }
    }
  }

  function setupNativeHls() {
    if (video && videoSrc?.status === "COMPLETE") {
      video.src = videoSrc.videoUrl;
      /**
       * @see https://stackoverflow.com/questions/33300294/html5-video-loadeddata-event-does-not-work-in-ios-safari/40006785#40006785
       * iOS에서는 재생 상호작용 전에 loadeddata 이벤트가 발생하지 않아 loadedmetadata 이벤트로 대체
       */
      video.addEventListener("loadedmetadata", () => {
        isPlayable = true;
      });
    }
  }

  function initializeHls(Hls: typeof HlsType) {
    if (video && videoSrc?.status === "COMPLETE") {
      hls = new Hls();
      if (hls) {
        hls.loadSource(videoSrc.videoUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          isPlayable = true;
        });
      }
    }
  }

  function handleVideoEndedEvent() {
    // 스와이퍼가 존재할 경우 영상이 종료되었을 때, 다음 슬라이드로 넘어간다.
    if (
      $swiperInstance &&
      $swiperInstance.autoplay &&
      $swiperOptions.ready &&
      $swiperLength > 1 &&
      !$swiperOptions.preventSlideOnVideoEnd
    ) {
      $swiperInstance.slideNext();
    }

    // loop 시 ended 옵션을 사용할 수 없어 커스텀 loop 기능으로 대체
    if (loop) {
      if ($swiperRealIndex === slideNodeIndex || !$swiperOptions.ready) {
        if (video) {
          video.play().catch((e) => {
            // eslint-disable-next-line no-console
            console.error(e);
          });
        }
      }
    }
  }

  function handleVideoTimeupdateEvent() {
    if (!video) {
      return;
    }

    const currentTime = video.currentTime;
    if (video.duration - 0.01 <= video.currentTime) {
      // 정상적으로 처리되었을 수 있으므로 0.01초 뒤에 다시 검증
      setTimeout(() => {
        if (!video) {
          return;
        }

        // video.currentTime이 0이라면 정상 재생된것으로 처리하지 않음
        // 0이 아닌데 duration보다 크거나, currentTime 변화가 없는 경우 loop 처리
        if (
          video.currentTime > video.duration ||
          (video.currentTime !== 0 && video.currentTime === currentTime)
        ) {
          // currentTime을 0으로 하면 영상이 처음으로 이동하나 가끔 글리치 현상이 나타남. pause로 대체
          // video.currentTime = 0;
          video.pause();
        }
      }, 0.01);
    }
  }

  $: {
    if ($rootShown) {
      isShown = true;
    }

    if (isShown) {
      if (isPlayable) {
        // 현재 캠페인에 스와이퍼가 존재할 경우 현재 노출되고 있는 화면의 영상만 재생시킨다.
        if ($swiperInstance && $swiperOptions.ready) {
          if ($swiperRealIndex === slideNodeIndex) {
            if (
              !$swiperOptions.preventSlideOnVideoEnd &&
              $swiperInstance.autoplay
            ) {
              $swiperInstance.autoplay.stop();
            }

            if (video?.paused && autoplay) {
              video
                ?.play()
                .then(() => {
                  isPlaying = true;

                  // 여기셔 실행 했는데 슬라이드가 넘어가서 현재 슬라이드가 아니게 되었을 때, 정지시키는 로직
                  if ($swiperRealIndex !== slideNodeIndex) {
                    video?.pause();
                    isPlaying = false;
                  }
                })
                .catch((e) => {
                  // eslint-disable-next-line no-console
                  console.error(e);
                });
            }
          } else {
            if (isPlaying) {
              video?.pause();
              isPlaying = false;
            }
          }
        } else {
          if (autoplay) {
            // eslint-disable-next-line no-console
            video?.play().catch((e) => console.error(e));
          }
        }
      }
    }
  }
  onDestroy(() => {
    // 컴포넌트가 파괴될 때 HLS 인스턴스 정리
    if (hls) {
      hls.destroy();
    }
  });
</script>

{#if $captureMode}
  <!-- 이미지 캡쳐모드일 경우 썸네일  -->
  <img
    alt={node.id}
    style={styleToString({ ...getVideoStyle(node) })}
    src={node.videoInfo?.thumbnailPath}
  />
{:else if videoSrc && videoSrc.width && videoSrc.height}
  <div
    use:detectReaction={{
      node,
      documentNode,
      onAction: reactionEventListener,
    }}
    style={styleToString({
      position: "relative",
      ...getVideoStyle(node),
    })}
  >
    <div
      style={styleToString({
        position: "relative",
        paddingBottom: `${(videoSrc.height / videoSrc.width) * 100}%`,
      })}
    >
      <video
        style={styleToString({
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          backgroundImage: `url(${node.videoInfo?.thumbnailPath ?? ""})`,
          backgroundSize: "cover",
        })}
        preload="auto"
        playsinline={autoplay}
        {muted}
        {controls}
        bind:this={video}
        poster={node.videoInfo?.thumbnailPath}
        on:ended={handleVideoEndedEvent}
        on:timeupdate={handleVideoTimeupdateEvent}
      >
      </video>
    </div>
  </div>
{:else}
  <div
    use:detectReaction={{
      node,
      documentNode,
      onAction: reactionEventListener,
    }}
    style={styleToString({
      ...getVideoStyle(node),
    })}
  >
    <video
      preload="auto"
      playsinline={autoplay}
      {muted}
      {controls}
      bind:this={video}
      poster={node.videoInfo?.thumbnailPath}
      on:ended={handleVideoEndedEvent}
    >
    </video>
  </div>
{/if}
